import {
  ChronosphereDataQueryDefinition,
  ContextLinkDefinition,
  JSONValue,
  PanelDefinition,
  PanelSpec,
  UnknownDefinition,
} from '@/core';
import { BasePanelProps, PanelChromeProps } from '../model';
import { CommonChartProps } from '../utils';

interface CreatePanelArgs<ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue> {
  kind: string;
  chartProps: ComponentProps;
  queries: UnknownDefinition[];
  mapPropsToSpec: (chartProps: Omit<ComponentProps, CommonChartProps>) => DashboardSpec;
  links?: ContextLinkDefinition[];
  panelChromeProps?: PanelChromeProps;
}

/**
 * Returns the dashboard panel definition given a chart's props.
 * Used to enable the "Add to dashboard" feature in viz panels.
 */
export const createPanelDefinitionFromVizPanel = <
  ComponentProps extends BasePanelProps,
  DashboardSpec extends JSONValue,
>(
  args: CreatePanelArgs<ComponentProps, DashboardSpec>
): PanelDefinition<DashboardSpec> => {
  const { kind, chartProps, queries, mapPropsToSpec, links, panelChromeProps } = args;

  const { addToDashboardOverrides, ...rest } = chartProps;
  const chartPropsWithOverrides = { ...rest, ...addToDashboardOverrides };
  const chartSpec = mapPropsToSpec(chartPropsWithOverrides as ComponentProps);
  const panelSpec: PanelSpec<DashboardSpec> = {
    display: {
      name: addToDashboardOverrides?.panelChromeProps?.title ?? panelChromeProps?.title ?? '',
      ...((panelChromeProps?.description || addToDashboardOverrides?.panelChromeProps?.description) && {
        description: addToDashboardOverrides?.panelChromeProps?.description ?? panelChromeProps?.description,
      }),
    },
    plugin: {
      kind: kind,
      spec: chartSpec,
    },
    queries: queries.map((q) => ({
      kind: 'DataQuery',
      spec: {
        plugin: q as ChronosphereDataQueryDefinition,
      },
    })),
    links: links ?? [],
  };

  const panelDefinition: PanelDefinition<DashboardSpec> = {
    kind: 'Panel',
    spec: panelSpec,
  };

  return panelDefinition;
};
